import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withHooks from '../../core/withHooks';
import ResetPasswordController from './reset-password.controller';
import InvalidInvitation from './invalid-invitation';
import ResetPasswordSuccess from './reset-password-success';
import { Button as AntButton, Input as AntInput } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import styles from './styles/reset-password.module.scss';

/**
 * @extends ClockoutController
 * @extends ResetPasswordController
 * @extends Component
 */
class ResetPassword extends Inherit(ResetPasswordController, Component) {
    state = {
        password: '',
        confirmPassword: '',
        loading: false,
        error: false,
        invitationValidating: false,
        invitationValid: null,
        invitationData: null,
        showSuccessScreen: false
    };

    render() {
        const { 
            password, 
            confirmPassword, 
            error, 
            loading, 
            invitationValidating,
            invitationValid,
            invitationData,
            showSuccessScreen
        } = this.state;

        // Show success screen if password reset was successful
        if (showSuccessScreen) {
            return <ResetPasswordSuccess />;
        }

        // Show loading while validating invitation
        if (invitationValidating) {
            return (
                <div className={styles.resetContainer}>
                    <div className={styles.leftSection}>
                        <div className={styles.logoContainer}>
                            <img src="/logo-color.png" alt="Clockout Logo" className={styles.logo} />
                        </div>
                        
                        <div className={styles.contentWrapper}>
                            <div className={styles.headerSection}>
                                <h1 className={styles.title}>Setting Up Your Password</h1>
                                <p className={styles.subtitle}>
                                    Please wait while we validate your reset password link and prepare your account.
                                </p>
                            </div>

                            <div className={styles.imageSection}>
                                <img src="/phone-ux.png" alt="Clockout App Preview" className={styles.phoneImage} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.formHeader}>
                            <span className={styles.alreadyHaveAccount}>Already have an account?</span>
                            <AntButton 
                                type="text" 
                                onClick={() => this.navigateToSignIn()}
                                className={styles.signInButton}
                            >
                                Sign in
                            </AntButton>
                        </div>

                        <div className={styles.resetCard}>
                            <div className={styles.loadingContent}>
                                <h2 className={styles.formTitle}>Validating Reset Link...</h2>
                                <p className={styles.loadingText}>Please wait while we validate your reset password link.</p>
                            </div>
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <span className={styles.copyright}>Copyright © 2025 Clockout Financial Inc.</span>
                        <div className={styles.footerLinks}>
                            <a href="/terms" className={styles.footerLink}>Terms and Conditions</a>
                            <a href="/privacy" className={styles.footerLink}>Privacy Policy</a>
                        </div>
                    </div>
                </div>
            );
        }

        // Show invalid invitation component if invitation is invalid
        if (invitationValid === false) {
            return <InvalidInvitation />;
        }

        // Show reset password form if invitation is valid
        if (invitationValid === true) {
            return (
                <div className={styles.resetContainer}>
                    <div className={styles.leftSection}>
                        <div className={styles.logoContainer}>
                            <img src="/logo-color.png" alt="Clockout Logo" className={styles.logo} />
                        </div>
                        
                        <div className={styles.contentWrapper}>
                            <div className={styles.headerSection}>
                                <h1 className={styles.title}>Welcome to Clockout</h1>
                                <p className={styles.subtitle}>
                                    Set up your password to complete your account setup and start using your Earned Wage Access (EWA) service.
                                </p>
                            </div>

                            <div className={styles.imageSection}>
                                <img src="/phone-ux.png" alt="Clockout App Preview" className={styles.phoneImage} />
                            </div>
                        </div>
                    </div>

                    <div className={styles.rightSection}>
                        <div className={styles.formHeader}>
                            <span className={styles.alreadyHaveAccount}>Already have an account?</span>
                            <AntButton 
                                type="text" 
                                onClick={() => this.navigateToSignIn()}
                                className={styles.signInButton}
                            >
                                Sign in
                            </AntButton>
                        </div>

                        <div className={styles.resetCard}>
                            <h2 className={styles.formTitle}>Set Your Password</h2>
                            <p className={styles.invitationEmail}>
                                Set up a new password for <strong>{invitationData?.email || 'your account'}</strong>
                            </p>

                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>New Password</label>
                                <AntInput.Password
                                    size="large"
                                    prefix={<LockOutlined className={styles.inputIcon} />}
                                    value={password || ''}
                                    onChange={(e) => this.setPassword(e.target.value)}
                                    placeholder="password"
                                    status={error ? 'error' : ''}
                                    className={styles.input}
                                />
                            </div>

                            <div className={styles.inputGroup}>
                                <label className={styles.inputLabel}>Confirm Password</label>
                                <AntInput.Password
                                    size="large"
                                    prefix={<LockOutlined className={styles.inputIcon} />}
                                    value={confirmPassword || ''}
                                    onChange={(e) => this.setConfirmPassword(e.target.value)}
                                    placeholder="password"
                                    status={error ? 'error' : ''}
                                    className={styles.input}
                                />
                            </div>

                            {error && (
                                <div className={styles.errorMessage}>
                                    Please ensure passwords match and are at least 8 characters long.
                                </div>
                            )}

                            <AntButton
                                type="primary"
                                size="large"
                                loading={loading}
                                onClick={() => this.resetPassword()}
                                className={styles.continueButton}
                                block
                            >
                                Complete Setup
                            </AntButton>
                        </div>
                    </div>

                    <div className={styles.footer}>
                        <span className={styles.copyright}>Copyright © 2025 Clockout Financial Inc.</span>
                        <div className={styles.footerLinks}>
                            <a href="/terms" className={styles.footerLink}>Terms and Conditions</a>
                            <a href="/privacy" className={styles.footerLink}>Privacy Policy</a>
                        </div>
                    </div>
                </div>
            );
        }

        // Show loading if still initializing
        return (
            <div className={styles.resetContainer}>
                <div className={styles.leftSection}>
                    <div className={styles.logoContainer}>
                        <img src="/logo-color.png" alt="Clockout Logo" className={styles.logo} />
                    </div>
                    
                    <div className={styles.contentWrapper}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>Reset Password</h1>
                            <p className={styles.subtitle}>
                                Choose a new password for your account.
                            </p>
                        </div>

                        <div className={styles.imageSection}>
                            <img src="/phone-ux.png" alt="Clockout App Preview" className={styles.phoneImage} />
                        </div>
                    </div>
                </div>

                <div className={styles.rightSection}>
                    <div className={styles.formHeader}>
                        <span className={styles.alreadyHaveAccount}>Already have an account?</span>
                        <AntButton 
                            type="text" 
                            onClick={() => this.navigateToSignIn()}
                            className={styles.signInButton}
                        >
                            Sign in
                        </AntButton>
                    </div>

                    <div className={styles.resetCard}>
                        <div className={styles.loadingContent}>
                            <h2 className={styles.formTitle}>Loading...</h2>
                            <p className={styles.loadingText}>Please wait while we load the reset password form.</p>
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <span className={styles.copyright}>Copyright © 2025 Clockout Financial Inc.</span>
                    <div className={styles.footerLinks}>
                        <a href="/terms" className={styles.footerLink}>Terms and Conditions</a>
                        <a href="/privacy" className={styles.footerLink}>Privacy Policy</a>
                    </div>
                </div>
            </div>
        );
    }
}

export default withHooks(ResetPassword);

